/**
 * Texturas compartidas.
 *
 * Esto es lo que hace que el dibujo aguante: todas las tarjetas de un mismo
 * sexo comparten UNA textura, asi que 80 personas (u 800) salen en unas pocas
 * llamadas de dibujo porque Pixi agrupa los sprites de la misma textura. Si
 * cada persona llevara su propio objeto de dibujo vectorial habria una
 * llamada por persona y se notaria.
 *
 * Las texturas se generan una vez al arrancar y no se vuelven a tocar.
 */
import { Graphics, Texture, type Renderer } from "pixi.js";
import type { Sexo } from "../core/model.js";
import { ALTO_NODO, ANCHO_NODO, RADIO_HOMBRE, RADIO_MUJER } from "../layout/medidas.js";
import { estiloDe } from "./tema.js";

/** Resolucion de las texturas de tarjeta: el doble, para que el zoom aguante. */
const RESOLUCION = 3;
/** Lado del punto minimo, en pixeles de textura. */
const LADO_PUNTO = 48;

export interface Texturas {
  /** La tarjeta completa, por sexo. */
  tarjeta: Record<string, Texture>;
  /** La silueta minima para cuando se esta muy alejado, por sexo. */
  punto: Record<string, Texture>;
  /** Resplandor suave, en blanco: se tinta al usarlo. */
  resplandor: Texture;
  /** Marco de seleccion. */
  seleccion: Texture;
}

const SEXOS: Sexo[] = ["M", "F", "U", "X"];

function dibujarSilueta(
  g: Graphics,
  sexo: Sexo,
  w: number,
  h: number,
): void {
  const e = estiloDe(sexo);
  switch (e.forma) {
    case "rectangulo":
      g.roundRect(0, 0, w, h, RADIO_HOMBRE);
      break;
    case "capsula":
      g.roundRect(0, 0, w, h, Math.min(RADIO_MUJER, h / 2));
      break;
    case "rombo":
      g.moveTo(w / 2, 0)
        .lineTo(w, h / 2)
        .lineTo(w / 2, h)
        .lineTo(0, h / 2)
        .closePath();
      break;
  }
}

export function crearTexturas(renderer: Renderer): Texturas {
  const tarjeta: Record<string, Texture> = {};
  const punto: Record<string, Texture> = {};

  for (const sexo of SEXOS) {
    const e = estiloDe(sexo);

    const g = new Graphics();
    dibujarSilueta(g, sexo, ANCHO_NODO, ALTO_NODO);
    g.fill({ color: e.relleno, alpha: 0.96 });
    dibujarSilueta(g, sexo, ANCHO_NODO, ALTO_NODO);
    g.stroke({ width: 1.6, color: e.borde, alpha: 0.85 });
    tarjeta[sexo] = renderer.generateTexture({ target: g, resolution: RESOLUCION });
    g.destroy();

    // El punto es la MISMA silueta en pequeno, no un circulo para todos: la
    // forma tiene que seguir distinguiendose cuando el texto ya no cabe.
    const p = new Graphics();
    dibujarSiluetaPunto(p, sexo, LADO_PUNTO);
    p.fill({ color: e.borde, alpha: 1 });
    punto[sexo] = renderer.generateTexture({ target: p, resolution: 2 });
    p.destroy();
  }

  const s = new Graphics();
  s.roundRect(-3, -3, ANCHO_NODO + 6, ALTO_NODO + 6, RADIO_HOMBRE + 3);
  s.stroke({ width: 2.4, color: 0xffffff, alpha: 1 });
  const seleccion = renderer.generateTexture({ target: s, resolution: RESOLUCION });
  s.destroy();

  return {
    tarjeta,
    punto,
    resplandor: crearResplandor(),
    seleccion,
  };
}

/** La silueta del punto: cuadrado, circulo o rombo segun el sexo. */
function dibujarSiluetaPunto(g: Graphics, sexo: Sexo, lado: number): void {
  const e = estiloDe(sexo);
  const r = lado / 2;
  switch (e.forma) {
    case "rectangulo":
      g.rect(lado * 0.1, lado * 0.1, lado * 0.8, lado * 0.8);
      break;
    case "capsula":
      g.circle(r, r, r * 0.86);
      break;
    case "rombo":
      g.moveTo(r, 0).lineTo(lado, r).lineTo(r, lado).lineTo(0, r).closePath();
      break;
  }
}

/**
 * Resplandor: un degradado radial en blanco, para tintarlo despues con el
 * color de cada sexo.
 *
 * Se hace con un degradado en un lienzo y no con un filtro de desenfoque a
 * proposito: un filtro por nodo obliga a renderizar cada uno a su propia
 * textura intermedia, que es justo lo que no puede permitirse si se quieren
 * 60 fps con cientos de nodos. Un sprite tintado es practicamente gratis.
 */
function crearResplandor(): Texture {
  const lado = 256;
  const lienzo = document.createElement("canvas");
  lienzo.width = lado;
  lienzo.height = lado;
  const ctx = lienzo.getContext("2d");
  if (!ctx) return Texture.WHITE;

  const r = lado / 2;
  const grad = ctx.createRadialGradient(r, r, 0, r, r, r);
  // Curva suave: nucleo tenue y caida larga, que es lo que da el halo de
  // Obsidian en vez de un circulo con borde.
  grad.addColorStop(0, "rgba(255,255,255,0.55)");
  grad.addColorStop(0.28, "rgba(255,255,255,0.26)");
  grad.addColorStop(0.58, "rgba(255,255,255,0.08)");
  grad.addColorStop(1, "rgba(255,255,255,0)");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, lado, lado);

  return Texture.from(lienzo);
}
